<template>
    <div class="page-wrap">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">Team</div>
                <div class="page-bar-main">Accountability Chart</div>
            </div>
        </div>

        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'" style="padding-bottom:0">
            <template v-if="charts.length">
                <div class="orgteam-bar">
                    <button v-for="(c, i) in charts" :key="i" type="button" class="orgteam-btn" :class="{ on: selectedChartID == c.ID }" @click="selectChart(c.ID)">{{ c.Name }}</button>
                    <button v-if="isAdmin" @click="newChart()" type="button" class="orgteam-btn on" style="pointer-events:auto">New Chart</button>
                    <button @click="shareChart()" type="button" class="orgteam-btn on" style="pointer-events:auto">Share</button>
                </div>
                <div v-if="isAdmin" style="margin-bottom:14px">
                    <a @click="editChart()">Edit chart</a>
                </div>
                <template v-if="rootNodes.length">
                    <div class="orgwrap">
                        <div class="tree acct-flip" :style="{ zoom: zoomLevels[zoomIndex] }">
                            <ul class="tree-ul">
                                <accountability-chart-node v-for="n in rootNodes" :key="n.EmployeeID" :node="n" />
                            </ul>
                        </div>
                    </div>
                    <!-- Sticks to the bottom of the screen while the chart runs past it -->
                    <div class="acct-zoom">
                        <button type="button" title="Zoom out" :disabled="zoomIndex == 0" @click="zoomIndex--"><svg-icon icon="zoomout" /></button>
                        <button type="button" class="acct-zoom-pct" title="Reset zoom" @click="zoomIndex = defaultZoomIndex">{{ Math.round(zoomLevels[zoomIndex] * 100) }}%</button>
                        <button type="button" title="Zoom in" :disabled="zoomIndex == zoomLevels.length - 1" @click="zoomIndex++"><svg-icon icon="zoomin" /></button>
                    </div>
                </template>
                <div v-else style="margin:20px 0;color:#a8a4a0">
                    This chart doesn't have anyone in it yet.
                    <span v-if="isAdmin"><a @click="editChart()">Add people</a>.</span>
                </div>
            </template>
            <div v-else style="margin:20px 0;color:#a8a4a0">
                No accountability charts yet.
                <span v-if="isAdmin"><a @click="newChart()">Create the first one</a>.</span>
            </div>
        </div>

        <div v-if="showShare" class="error-msg-wrap">
            <div class="form-wrapper slide-up">
                <div class="form-close">
                    <button type="button" class="form-close-x" title="Close" @click="() => { showShare = false }">&times;</button>
                </div>
                <div style="margin-bottom:15px;text-decoration:underline"><a @click.prevent="copyLink()">Copy Link</a></div>
                <h3>Email</h3>
                <input type="text" v-model="shareData.email" placeholder="Email" />
                <h3>Phone</h3>
                <phone-input v-model="shareData.phone" placeholder="Phone" />
                <input :class="sharing ? 'disable-button' : ''" type="submit" value="Send" @click="sendShare()" />
            </div>
        </div>

        <div v-if="showEditChart" class="error-msg-wrap">
            <div class="form-wrapper slide-up" style="width:1100px;max-width:95%">
                <div class="form-close">
                    <button type="button" class="form-close-x" title="Close" @click="() => { showEditChart = false }">&times;</button>
                </div>
                <h3 style="margin-top:0">{{ editingChart.ID ? 'Edit chart' : 'New chart' }}</h3>
                <input type="text" placeholder="e.g. Production" v-model="editingChart.Name" />

                <div class="acct-editor-body">
                    <div>
                        <div class="acct-editor-pane-label">People in this chart</div>
                        <div class="acct-add-row">
                            <select v-model="addPersonID">
                                <option value="">Select someone to add…</option>
                                <option v-for="(e, i) in availableEmployees" :key="i" :value="e.ID">{{ e.Name }}<template v-if="roleTitle(e)"> — {{ roleTitle(e) }}</template></option>
                            </select>
                            <input style="width:auto" type="submit" value="Add" :class="!addPersonID ? 'disable-button' : ''" @click="addPerson()" />
                        </div>

                        <div v-if="cycleEmployeeID" class="acct-cycle-banner">⚠ {{ nameFor(cycleEmployeeID) }}'s "reports to" chain loops back to themself — fix it before saving.</div>

                        <div v-if="editingChart.nodes.length" class="acct-roster-list">
                            <div v-for="n in editingChart.nodes" :key="n.EmployeeID" class="acct-roster-row">
                                <div class="acct-ini">{{ initialsFor(n.Name) }}</div>
                                <div class="acct-who">
                                    <div class="acct-n">{{ n.Name }}</div>
                                    <div class="acct-t">{{ roleTitle(n) }}</div>
                                </div>
                                <select :class="{ 'cycle-error': cycleEmployeeID == n.EmployeeID }" @change="setManager(n.EmployeeID, $event.target.value)">
                                    <option value="" :selected="!n.ManagerEmployeeID">— Top of chart —</option>
                                    <option v-for="(o, j) in otherNodes(n.EmployeeID)" :key="j" :value="o.EmployeeID" :selected="o.EmployeeID == n.ManagerEmployeeID">{{ o.Name }}</option>
                                </select>
                                <button type="button" class="acct-remove-btn" title="Remove from chart" @click="removeNode(n.EmployeeID)">✕</button>
                            </div>
                        </div>
                        <div v-else class="acct-roster-empty">No one's in this chart yet — add people above.</div>

                        <a v-if="editingChart.ID" class="acct-delete-link" @click="() => { showConfirmDelete = true }">Delete this chart</a>
                    </div>

                    <div style="overflow:auto">
                        <div class="acct-editor-pane-label">Live preview — how everyone will see it</div>
                        <div class="acct-editor-preview">
                            <div v-if="editorRootNodes.length" class="tree acct-flip">
                                <ul class="tree-ul">
                                    <accountability-chart-node v-for="n in editorRootNodes" :key="n.EmployeeID" :node="n" />
                                </ul>
                            </div>
                            <div v-else class="acct-roster-empty">Nothing to preview yet.</div>
                        </div>
                    </div>
                </div>

                <input style="margin-top:25px" :class="disableButton ? 'disable-button' : ''" type="submit" value="Save chart" @click="saveChart()" />
            </div>
        </div>

        <confirm-delete v-if="showConfirmDelete" @confirm="deleteChart()" @cancel="() => { showConfirmDelete = false; }" msg="Are you sure you would like to delete this chart?"/>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import PhoneInput from '@/components/PhoneInput.vue';
import { normalizePhone, PHONE_ERROR } from '@/phone';
import AccountabilityChartNode from '@/components/AccountabilityChartNode.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import SvgIcon from '@/components/SvgIcon.vue';
import { seatTitle } from '@/roleSheet';

// Three steps out and three steps in from normal size
const ZOOM_LEVELS = [0.5, 0.65, 0.8, 1, 1.25, 1.5, 1.75];
const DEFAULT_ZOOM_INDEX = ZOOM_LEVELS.indexOf(1);

export default defineComponent({
    name: 'AccountabilityChartView',
    components: {
        PhoneInput,
        AccountabilityChartNode,
        ConfirmDelete,
        SvgIcon
    },
    data() {
        return {
            zoomLevels: ZOOM_LEVELS,
            defaultZoomIndex: DEFAULT_ZOOM_INDEX,
            zoomIndex: DEFAULT_ZOOM_INDEX,
            charts: [] as any,
            selectedChartID: null as any,
            currentNodes: [] as any,
            employeesAll: [] as any,
            showEditChart: false,
            disableButton: false,
            addPersonID: '' as any,
            editingChart: {
                ID: null as any,
                Name: null as any,
                nodes: [] as any
            },
            showConfirmDelete: false,
            showShare: false,
            sharing: false,
            shareData: {
                email: '',
                phone: ''
            }
        }
    },
    computed: {
        isAdmin(): boolean {
            return this.$store.state.user.teamHubRole == 'Admin';
        },
        rootNodes(): any {
            return this.buildTree(this.currentNodes);
        },
        editorRootNodes(): any {
            return this.buildTree(this.editingChart.nodes);
        },
        availableEmployees(): any {
            const inChart = new Set(this.editingChart.nodes.map((n: any) => '' + n.EmployeeID));
            return this.employeesAll.filter((e: any) => !inChart.has(e.ID));
        },
        cycleEmployeeID(): any {
            return this.detectCycle(this.editingChart.nodes);
        }
    },
    async created() {
        while(!this.$store.state.allNodesLoaded) {
            await new Promise(resolve => setTimeout(resolve, 200));
        }

        await this.loadCharts();

        this.$store.state.routeLoaded = true;
    },
    methods: {
        // From the person's seat on the role sheet, not the employee record's job title
        roleTitle(person: any): string {
            return seatTitle(this.$store.state.allNodes, person.RoleSheetSlug, person.RoleSheetColID);
        },
        buildTree(nodes: any) {
            const byManager: any = {};
            for(const n of nodes) {
                const key = n.ManagerEmployeeID || 'root';
                if(!byManager[key]) {
                    byManager[key] = [];
                }
                byManager[key].push(n);
            }

            const attachChildren = (node: any) => {
                return {
                    ...node,
                    children: (byManager[node.EmployeeID] || []).map(attachChildren)
                };
            }

            return (byManager['root'] || []).map(attachChildren);
        },
        detectCycle(nodes: any) {
            for(const n of nodes) {
                const seen = new Set();
                let cur = n;
                while(cur && cur.ManagerEmployeeID) {
                    if(seen.has(cur.EmployeeID)) {
                        return n.EmployeeID;
                    }
                    seen.add(cur.EmployeeID);
                    cur = nodes.find((x: any) => x.EmployeeID == cur.ManagerEmployeeID);
                }
            }
            return null;
        },
        otherNodes(employeeID: number) {
            return this.editingChart.nodes.filter((o: any) => o.EmployeeID != employeeID);
        },
        initialsFor(name: string) {
            if(!name) {
                return '';
            }
            const parts = name.trim().split(/\s+/);
            if(parts.length == 1) {
                return parts[0].substring(0, 2).toUpperCase();
            }
            return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
        },
        nameFor(employeeID: number) {
            const node = this.editingChart.nodes.find((n: any) => n.EmployeeID == employeeID);
            return node ? node.Name : '';
        },
        async loadCharts() {
            await axios.get(`${this.$store.state.apiUrl}/api/accountabilitycharts`)
                .then(async resp => {
                    this.charts = resp.data;
                    if(this.charts.length) {
                        // shared links open a specific chart via ?chart=ID
                        const shared = this.charts.find((c: any) => c.ID == this.$route.query.chart);
                        await this.selectChart(shared ? shared.ID : this.charts[0].ID);
                    }
                })
                .catch(e => {
                    console.log(e);
                })
        },
        async loadEmployeesAll() {
            if(!this.isAdmin || this.employeesAll.length) {
                return;
            }

            await axios.get(`${this.$store.state.apiUrl}/api/employees`)
                .then(resp => {
                    this.employeesAll = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })
        },
        shareChart() {
            this.shareData = {
                email: '',
                phone: ''
            }
            this.showShare = true;
        },
        copyLink() {
            navigator.clipboard.writeText(`${window.location.origin}/accountability-chart?chart=${this.selectedChartID}`);
        },
        async sendShare() {
            if(this.sharing) {
                return;
            }
            let query = '?z=t';
            if(!this.shareData.email && !this.shareData.phone) {
                alert('Enter an email and/or phone to share this with');
                return;
            }
            if(this.shareData.email) {
                if(!this.shareData.email.includes('@') || !this.shareData.email.includes('.')) {
                    alert('Make sure to use a valid email');
                    return;
                }
                this.shareData.email = this.shareData.email.trim();
                query += `&email=${encodeURIComponent(this.shareData.email)}`;
            }
            if(this.shareData.phone) {
                const sharePhone = normalizePhone(this.shareData.phone);
                if(!sharePhone) {
                    alert(PHONE_ERROR);
                    return;
                }
                query += `&phone=${encodeURIComponent(sharePhone)}`;
            }

            this.sharing = true;
            await axios.get(`${this.$store.state.apiUrl}/api/accountabilitycharts/${this.selectedChartID}/share${query}`)
                .then(resp => {
                    alert('Accountability Chart has been shared!');
                    this.showShare = false;
                })
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert(e?.response?.data || 'There was an error')
                })
            this.sharing = false;
        },
        async selectChart(id: number) {
            this.selectedChartID = id;
            await axios.get(`${this.$store.state.apiUrl}/api/accountabilitycharts/${id}`)
                .then(resp => {
                    this.currentNodes = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })
        },
        async newChart() {
            await this.loadEmployeesAll();
            this.addPersonID = '';
            this.editingChart = { ID: null, Name: null, nodes: [] };
            this.showEditChart = true;
        },
        async editChart() {
            await this.loadEmployeesAll();
            const chart = this.charts.find((c: any) => c.ID == this.selectedChartID);
            this.addPersonID = '';

            // Re-fetch this chart's nodes rather than trusting `currentNodes` as-is -- if "Edit
            // chart" gets clicked right after switching tabs, the selectChart() fetch for the
            // newly-picked chart can still be in flight, and `currentNodes` would still hold the
            // previous chart's (or no) roster. That stale/empty roster made every employee look
            // "available" to add, even ones already in the chart being edited.
            await this.selectChart(chart.ID);

            this.editingChart = {
                ID: chart.ID,
                Name: chart.Name,
                nodes: this.dedupeNodes(JSON.parse(JSON.stringify(this.currentNodes)))
            };
            this.showEditChart = true;
        },
        dedupeNodes(nodes: any) {
            // Defensive cleanup: if a chart somehow already has the same person more than once
            // (e.g. leftover data from before this safeguard existed), drop the extras on load so
            // the editor and tree can't render/act on duplicate EmployeeIDs. Saving afterward
            // persists the cleaned-up list.
            const seen = new Set();
            return nodes.filter((n: any) => {
                if(seen.has(n.EmployeeID)) {
                    return false;
                }
                seen.add(n.EmployeeID);
                return true;
            });
        },
        addPerson() {
            if(!this.addPersonID) {
                return;
            }
            const emp = this.employeesAll.find((e: any) => e.ID == this.addPersonID);
            this.editingChart.nodes.push({
                EmployeeID: emp.ID,
                Name: emp.Name,
                RoleSheetSlug: emp.RoleSheetSlug,
                RoleSheetColID: emp.RoleSheetColID,
                ManagerEmployeeID: null
            });
            this.addPersonID = '';
        },
        removeNode(employeeID: number) {
            this.editingChart.nodes = this.editingChart.nodes.filter((n: any) => n.EmployeeID != employeeID);
            for(const n of this.editingChart.nodes) {
                if(n.ManagerEmployeeID == employeeID) {
                    n.ManagerEmployeeID = null;
                }
            }
        },
        setManager(employeeID: number, managerID: any) {
            const node = this.editingChart.nodes.find((n: any) => n.EmployeeID == employeeID);
            node.ManagerEmployeeID = managerID ? Number(managerID) : null;
        },
        async saveChart() {
            if(!this.editingChart.Name) {
                alert('Add a name for this chart');
                return;
            }
            if(this.cycleEmployeeID) {
                alert('Fix the reporting loop before saving');
                return;
            }

            this.disableButton = true;

            await axios.post(`${this.$store.state.apiUrl}/api/accountabilitycharts`, { ID: this.editingChart.ID, Name: this.editingChart.Name })
                .then(async resp => {
                    const chartID = this.editingChart.ID || resp.data.ID;
                    const nodes = this.editingChart.nodes.map((n: any, i: number) => ({
                        EmployeeID: n.EmployeeID,
                        ManagerEmployeeID: n.ManagerEmployeeID,
                        SortOrder: i
                    }));

                    await axios.post(`${this.$store.state.apiUrl}/api/accountabilitycharts/${chartID}/nodes`, { nodes })
                        .then(async () => {
                            this.showEditChart = false;
                            await this.loadCharts();
                            await this.selectChart(chartID);
                        })
                        .catch(e => {
                            console.log(e);
                            alert('There was an error');
                        })
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error');
                })

            this.disableButton = false;
        },
        async deleteChart() {
            await axios.delete(`${this.$store.state.apiUrl}/api/accountabilitycharts/${this.editingChart.ID}`)
                .then(async () => {
                    this.showEditChart = false;
                    this.selectedChartID = null;
                    this.currentNodes = [];
                    await this.loadCharts();
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error');
                })

            this.showConfirmDelete = false;
        }
    }
});
</script>
